import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Send, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const AVATAR = "https://static.prod-images.emergentagent.com/jobs/51fe5950-e829-4696-b661-6142eda1be8b/images/d323d2b517bc51ed4ff656d5e2d1dc9d1b46be19c1c46611db4fe0967830d97f.jpeg";
const GREETING = "Hi, I'm Brian 🏍️ — The Chairman here. Ask me anything about advanced training, the RoSPA test, events or joining the club.";

const getSession = () => {
  let s = localStorage.getItem("brian_session");
  if (!s) {
    s = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);
    localStorage.setItem("brian_session", s);
  }
  return s;
};

export default function AskBrian() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSession());
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(err.response?.data?.detail) || "Sorry, I couldn't answer just now. Please try the Contact page." }]);
    } finally {
      setLoading(false);
    }
  };

  if (pathname.startsWith("/member") || pathname.startsWith("/admin")) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ask-brian-button"
          aria-label="Ask Brian"
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 rounded-full bg-[#0F2744] text-white pl-1.5 pr-4 py-1.5 shadow-xl hover:bg-[#0B192C] transition-colors"
        >
          <img src={AVATAR} alt="Brian" className="h-10 w-10 rounded-full object-cover border-2 border-amber-400" />
          <span className="text-sm font-bold">Ask Brian</span>
        </button>
      )}

      {open && (
        <div
          data-testid="ask-brian-panel"
          className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-96 h-[32rem] max-h-[80vh] flex flex-col rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4"
        >
          <div className="flex items-center gap-3 bg-[#0F2744] px-4 py-3">
            <img src={AVATAR} alt="Brian" className="h-10 w-10 rounded-full object-cover border-2 border-amber-400" />
            <div className="flex-1">
              <div className="text-white font-bold leading-tight">Ask Brian</div>
              <div className="text-amber-400 text-xs">RoSPA rider · here to help</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-300 hover:text-white" aria-label="Close chat" data-testid="ask-brian-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="ask-brian-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0F2744] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-[#0F2744]" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={send} className="flex items-center gap-2 border-t border-slate-200 p-3 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about training, events, joining…"
              className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2744]/20"
              data-testid="ask-brian-input"
            />
            <button type="submit" disabled={loading || !input.trim()} className="h-10 w-10 shrink-0 rounded-full bg-amber-500 hover:bg-amber-600 text-[#0F2744] flex items-center justify-center disabled:opacity-50" data-testid="ask-brian-send" aria-label="Send">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
